import { z } from 'zod';
/**
 * UUID identifier — UUIDv7 is generated app-side (ADR-006); on the wire it is
 * a standard UUID string and `z.string().uuid()` accepts v1–v8.
 */
export const uuidSchema = z.string().uuid();
/**
 * ISO 8601 timestamp string. Backend converts to Date at the boundary.
 */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * Local time of day in `HH:MM` 24h format.
 */
export const localTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM 24h time');
/**
 * SHA-256 in lowercase hex (64 chars).
 */
export const sha256HexSchema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Expected lowercase hex sha-256');
/**
 * Cursor-based pagination query params (admin-arch §3.0.1).
 * Cursor is an opaque base64 string produced by the backend.
 */
export const paginationSchema = z.object({
    cursor: z.string().optional(),
    limit: z.number().int().positive().max(200).default(50),
    sort_by: z.string().optional(),
    sort_dir: z.enum(['asc', 'desc']).default('asc'),
});
/**
 * Generic paginated list response wrapper. Use `paginatedListSchema(itemSchema)`
 * to get a typed response for a specific resource.
 */
export const paginatedListSchema = (itemSchema) => z.object({
    items: z.array(itemSchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().optional(),
});
/**
 * Error envelope (admin-arch §3.0.2 — simplified RFC 7807). All API errors
 * follow this shape so frontend can render uniformly.
 */
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
        trace_id: z.string().uuid().optional(),
    }),
});
/**
 * Idempotency-Key header value (mutation endpoints, e.g. payments / bulk).
 */
export const idempotencyKeyHeaderSchema = z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/, 'Idempotency-Key must be URL-safe');
/**
 * Pre-signed URL response — used by upload init and download-link endpoints.
 */
export const signedUrlSchema = z.object({
    url: z.string().url(),
    method: z.enum(['GET', 'PUT', 'POST']).default('GET'),
    headers: z.record(z.string(), z.string()).optional(),
    expires_at: isoDateTimeSchema,
});
//# sourceMappingURL=common.js.map